// =============================================================================
// WAR GAME TYPES - Shared type definitions & Zod schemas
// =============================================================================

import { z } from 'zod';

// =============================================================================
// CORE GAME TYPES
// =============================================================================

/**
 * Рівень терміновості ситуації
 */
export const UrgencyLevelSchema = z.enum(['CRITICAL', 'HIGH', 'MEDIUM']);
export type UrgencyLevel = z.infer<typeof UrgencyLevelSchema>;

/**
 * Вплив рішення на параметри
 */
export const ImpactSchema = z.object({
  militaryStrength: z.number().min(-25).max(25),
  economy: z.number().min(-25).max(25),
  resilience: z.number().min(-25).max(25),
  westernSupport: z.number().min(-25).max(25),
  trust: z.number().min(-25).max(25),
});
export type Impact = z.infer<typeof ImpactSchema>;

/**
 * Варіант дії гравця
 */
export const OptionSchema = z.object({
  id: z.string(),
  text: z.string(),
  description: z.string().optional(),
  impact: ImpactSchema,
});
export type Option = z.infer<typeof OptionSchema>;

/**
 * Повідомлення в історії розмови
 */
export const MessageSchema = z.object({
  role: z.enum(['user', 'assistant']),
  content: z.string(),
});
export type Message = z.infer<typeof MessageSchema>;

/**
 * Ігровий сценарій
 */
export const GameScenarioSchema = z.object({
  decisionNumber: z.number(),
  date: z.string(),
  location: z.string(),
  urgency: UrgencyLevelSchema,
  briefing: z.string(),
  options: z.array(OptionSchema).min(2).max(5),
});
export type GameScenario = z.infer<typeof GameScenarioSchema>;

/**
 * Запис про зроблений вибір
 */
export const DecisionRecordSchema = z.object({
  decisionNumber: z.number(),
  date: z.string(),
  chosenOption: OptionSchema,
  consequences: z.string(),
});
export type DecisionRecord = z.infer<typeof DecisionRecordSchema>;

// =============================================================================
// LLM RESPONSE TYPES
// =============================================================================

/**
 * Відповідь від LLM (внутрішній формат)
 */
export const LLMResponseSchema = z.object({
  consequences: z.string(),
  newSituation: z.object({
    date: z.string(),
    location: z.string(),
    briefing: z.string(),
    urgency: UrgencyLevelSchema,
    options: z.array(OptionSchema).min(2).max(5),
  }),
});
export type LLMResponse = z.infer<typeof LLMResponseSchema>;

/**
 * Структурована LLM відповідь для двофазної генерації:
 * тільки те, що складно надійно витягнути з нарративу.
 */
export const LLMStructuredScenarioSchema = z.object({
  newSituation: z.object({
    date: z.string(),
    location: z.string(),
    briefing: z.string(),
    urgency: UrgencyLevelSchema,
    options: z.array(OptionSchema).min(2).max(5),
  }),
});
export type LLMStructuredScenario = z.infer<typeof LLMStructuredScenarioSchema>;

/**
 * Backwards-compatible aliases (legacy naming)
 */
export const ClaudeResponseSchema = LLMResponseSchema;
export type ClaudeResponse = LLMResponse;

// =============================================================================
// API REQUEST/RESPONSE SCHEMAS
// =============================================================================

/**
 * Моделі, які підтримуємо для підбору в UI (OpenRouter IDs)
 */
export const SupportedModelSchema = z.enum([
  'meta-llama/llama-4-maverick',
  'meta-llama/llama-3.3-70b-instruct',
  'deepseek/deepseek-v3.2',
  'xiaomi/mimo-v2-flash:free',
  'x-ai/grok-4.1-fast',
  'mistralai/mistral-nemo',
  'openai/gpt-5-mini',
  'google/gemini-3-flash-preview',
  'qwen/qwen3-32b',
]);
export type SupportedModel = z.infer<typeof SupportedModelSchema>;

/**
 * Список моделей для UI
 */
export const SUPPORTED_MODELS = SupportedModelSchema.options;

/**
 * Запит на генерацію наступного сценарію
 */
export const NextScenarioRequestSchema = z.object({
  conversationHistory: z.array(MessageSchema),
  playerChoice: z.string().min(1),
  currentDecision: z.number().min(1).max(20),
  model: SupportedModelSchema.optional(),
  currentStats: z
    .object({
      militaryStrength: z.number().min(0).max(100),
      economy: z.number().min(0).max(100),
      resilience: z.number().min(0).max(100),
      westernSupport: z.number().min(0).max(100),
      trust: z.number().min(0).max(100),
    })
    .optional(),
});
export type NextScenarioRequest = z.infer<typeof NextScenarioRequestSchema>;

/**
 * Відповідь з новим сценарієм
 */
export const NextScenarioResponseSchema = z.object({
  success: z.literal(true),
  scenario: GameScenarioSchema,
  consequences: z.string(),
});
export type NextScenarioResponse = z.infer<typeof NextScenarioResponseSchema>;

/**
 * Запит на генерацію фіналу
 */
export const EndingRequestSchema = z.object({
  conversationHistory: z.array(MessageSchema),
  model: SupportedModelSchema.optional(),
  finalStats: z.object({
    militaryStrength: z.number().min(0).max(100),
    economy: z.number().min(0).max(100),
    resilience: z.number().min(0).max(100),
    westernSupport: z.number().min(0).max(100),
    trust: z.number().min(0).max(100),
  }),
});
export type EndingRequest = z.infer<typeof EndingRequestSchema>;

/**
 * Відповідь з фіналом
 */
export const EndingResponseSchema = z.object({
  success: z.literal(true),
  summary: z.string(),
});
export type EndingResponse = z.infer<typeof EndingResponseSchema>;

/**
 * Помилка API
 */
export const ErrorResponseSchema = z.object({
  success: z.literal(false),
  error: z.string(),
  code: z.string().optional(),
});
export type ErrorResponse = z.infer<typeof ErrorResponseSchema>;

/**
 * Відповідь з початковим сценарієм
 */
export const InitResponseSchema = z.object({
  success: z.literal(true),
  scenario: GameScenarioSchema,
  config: z.object({
    maxDecisions: z.number(),
    initialParameters: z.object({
      militaryStrength: z.number(),
      economy: z.number(),
      resilience: z.number(),
      westernSupport: z.number(),
      trust: z.number(),
    }),
  }),
});
export type InitResponse = z.infer<typeof InitResponseSchema>;

// =============================================================================
// UI CONSTANTS
// =============================================================================

/**
 * Початкові значення параметрів
 */
export const INITIAL_PARAMETERS = {
  militaryStrength: 60,
  economy: 70,
  resilience: 75,
  westernSupport: 50,
  trust: 65,
} as const;

/**
 * Максимальна кількість рішень у грі
 */
export const MAX_DECISIONS = 20;

/**
 * Назви параметрів для відображення
 */
export const PARAMETER_LABELS: Record<
  keyof Impact,
  { label: string; emoji: string }
> = {
  militaryStrength: { label: 'Військова сила', emoji: '🪖' },
  economy: { label: 'Економіка', emoji: '💰' },
  resilience: { label: 'Стійкість нації', emoji: '💪' },
  westernSupport: { label: 'Підтримка Заходу', emoji: '🤝' },
  trust: { label: 'Довіра до влади', emoji: '🏛️' },
};

/**
 * Діапазон допустимих значень impact
 */
export const IMPACT_RANGE = {
  min: -25,
  max: 25,
} as const;

/**
 * Кампанія (збір або петиція)
 */
export type CampaignType = 'donation' | 'petition';

export interface DonationCampaign {
  id?: string;      // The nanoid (for backwards compat with old campaigns)
  slug?: string;    // Custom URL slug for subdomain
  url: string;
  title?: string;
  description?: string;
  type?: CampaignType;
}

/**
 * Фрази для детектування емоційних піків (втрата людей)
 * Використовується для показу Action Strip
 */

// =============================================================================
// GAME OVER & VITAL THRESHOLDS
// =============================================================================

/**
 * Пороги для параметрів гри
 */
export const VITAL_THRESHOLDS = {
  /** Гра закінчена - критичний провал */
  CRITICAL: 15,
  /** Серйозні попередження - загроза краху */
  WARNING: 30,
  /** Низький рівень - починаються наслідки */
  LOW: 45,
} as const;

/**
 * Типи програшів залежно від параметру
 */
export const GameOverTypeSchema = z.enum([
  'MILITARY_DEFEAT',
  'ECONOMIC_COLLAPSE',
  'CIVIL_UNREST',
  'ISOLATION',
  'GOVERNMENT_CRISIS',
]);
export type GameOverType = z.infer<typeof GameOverTypeSchema>;

/**
 * Маппінг параметрів до типів програшу
 */
export const GAME_OVER_MAP: Record<keyof Impact, GameOverType> = {
  militaryStrength: 'MILITARY_DEFEAT',
  economy: 'ECONOMIC_COLLAPSE',
  resilience: 'CIVIL_UNREST',
  westernSupport: 'ISOLATION',
  trust: 'GOVERNMENT_CRISIS',
};

/**
 * Тексти для кожного типу програшу
 */
export const GAME_OVER_TEXTS: Record<
  GameOverType,
  { title: string; description: string }
> = {
  MILITARY_DEFEAT: {
    title: '💀 ВІЙСЬКОВА ПОРАЗКА',
    description:
      'Збройні сили України більше не здатні протистояти агресору. Фронт прорвано на всіх напрямках. Київ в оточенні.',
  },
  ECONOMIC_COLLAPSE: {
    title: '📉 ЕКОНОМІЧНИЙ КОЛАПС',
    description:
      'Економіка повністю зруйнована. Інфраструктура не функціонує. Держава не здатна забезпечити базові потреби армії та населення.',
  },
  CIVIL_UNREST: {
    title: '🔥 ГРОМАДЯНСЬКІ ЗАВОРУШЕННЯ',
    description:
      'Стійкість нації зламана. Масові протести та дезертирство паралізували опір. Суспільство втратило волю до боротьби.',
  },
  ISOLATION: {
    title: '🌍 МІЖНАРОДНА ІЗОЛЯЦІЯ',
    description:
      'Захід припинив будь-яку підтримку. Без зброї, боєприпасів та фінансової допомоги Україна залишилась сам на сам з агресором.',
  },
  GOVERNMENT_CRISIS: {
    title: '🏛️ КРИЗА ВЛАДИ',
    description:
      'Довіра до керівництва знищена. Спроба державного перевороту розколола країну. Централізоване управління обороною втрачено.',
  },
};

/**
 * Відповідь при програші
 */
export const GameOverResponseSchema = z.object({
  success: z.literal(true),
  gameOver: z.literal(true),
  type: GameOverTypeSchema,
  title: z.string(),
  description: z.string(),
  finalStats: z.object({
    militaryStrength: z.number(),
    economy: z.number(),
    resilience: z.number(),
    westernSupport: z.number(),
    trust: z.number(),
  }),
});
export type GameOverResponse = z.infer<typeof GameOverResponseSchema>;

// =============================================================================
// GAME STATE (for UI)
// =============================================================================

/**
 * Повний стан гри
 */
export interface GameState {
  phase: 'intro' | 'playing' | 'ending' | 'gameOver';
  decisionNumber: number;
  currentScenario: GameScenario | null;
  militaryStrength: number;
  economy: number;
  resilience: number;
  westernSupport: number;
  trust: number;
  conversationHistory: Message[];
  decisionHistory: DecisionRecord[];
  isLoading: boolean;
  error: string | null;
  gameOverType?: GameOverType;
}
